import { useState, useRef, useCallback } from 'react';

export type GeolocationStatus = 'idle' | 'loading' | 'success' | 'denied' | 'unavailable';

export interface GeolocationState {
  status: GeolocationStatus;
  position: GeolocationPosition | null;
}

const GEOLOC_CONSENT_KEY = 'geoloc_granted';

function createUserDot(map: google.maps.Map, latitude: number, longitude: number, markerRef: React.MutableRefObject<google.maps.marker.AdvancedMarkerElement | null>, panTo: boolean) {
  const latLng = { lat: latitude, lng: longitude };

  if (markerRef.current) {
    markerRef.current.map = null;
  }

  const dot = document.createElement('div');
  dot.style.cssText = `
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #1a73e8;
    border: 3px solid #fff;
    box-shadow: 0 2px 8px rgba(26,115,232,0.5);
    position: relative;
  `;
  const pulse = document.createElement('div');
  pulse.style.cssText = `
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: rgba(26,115,232,0.25);
    animation: geoloc-pulse 1.8s ease-out infinite;
  `;
  dot.appendChild(pulse);

  if (!document.getElementById('geoloc-pulse-style')) {
    const style = document.createElement('style');
    style.id = 'geoloc-pulse-style';
    style.textContent = `
      @keyframes geoloc-pulse {
        0% { transform: translate(-50%,-50%) scale(1); opacity: 0.6; }
        100% { transform: translate(-50%,-50%) scale(2.5); opacity: 0; }
      }
    `;
    document.head.appendChild(style);
  }

  markerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
    map,
    position: latLng,
    content: dot,
    zIndex: 500,
    title: ''
  });

  if (panTo) {
    map.panTo(latLng);
    map.setZoom(15);
  }
}

export function useGeolocation() {
  const [state, setState] = useState<GeolocationState>({
    status: 'idle',
    position: null
  });
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);

  const locate = useCallback((
    map: google.maps.Map | null,
    onSuccess?: (position: GeolocationPosition) => void
  ) => {
    if (!navigator.geolocation) {
      setState(s => ({ ...s, status: 'unavailable' }));
      return;
    }

    setState(s => ({ ...s, status: 'loading' }));

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setState({ status: 'success', position });
        localStorage.setItem(GEOLOC_CONSENT_KEY, '1');

        if (map) {
          createUserDot(map, position.coords.latitude, position.coords.longitude, markerRef, true);
        }

        onSuccess?.(position);
      },
      (error) => {
        localStorage.removeItem(GEOLOC_CONSENT_KEY);
        if (error.code === error.PERMISSION_DENIED) {
          setState(s => ({ ...s, status: 'denied' }));
        } else {
          setState(s => ({ ...s, status: 'unavailable' }));
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const autoLocate = useCallback((
    map: google.maps.Map | null
  ) => {
    if (!navigator.geolocation) return;
    if (!localStorage.getItem(GEOLOC_CONSENT_KEY)) {
      locate(map);
      return;
    }

    setState(s => ({ ...s, status: 'loading' }));

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setState({ status: 'success', position });

        if (map) {
          createUserDot(map, position.coords.latitude, position.coords.longitude, markerRef, false);
        }
      },
      (error) => {
        localStorage.removeItem(GEOLOC_CONSENT_KEY);
        if (error.code === error.PERMISSION_DENIED) {
          setState(s => ({ ...s, status: 'denied' }));
        } else {
          setState(s => ({ ...s, status: 'unavailable' }));
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [locate]);

  const clearMarker = useCallback(() => {
    if (markerRef.current) {
      markerRef.current.map = null;
      markerRef.current = null;
    }
    setState({ status: 'idle', position: null });
  }, []);

  return { ...state, locate, autoLocate, clearMarker };
}
